import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAuthHeaders, logout } from '../lib/auth';
import {
  Copy,
  CheckCircle,
  Loader2,
  LogOut,
  User,
  FileUp,
  ArrowLeft,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import mammoth from 'mammoth';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';

GlobalWorkerOptions.workerSrc =
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Deep yellow palette
const Y = {
  base:   '#d4a017',
  dark:   '#a87c10',
  light:  '#f0c040',
  bg:     '#0f1200',
  border: 'rgba(212,160,23,0.25)',
  text:   '#f5e6b0',
  dim:    'rgba(245,230,176,0.45)',
};

function XLogo({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function wordCount(text) {
  return text.trim() ? text.trim().split(/\s+/).length : 0;
}

export default function SocialPostsGeneratorPage() {
  const navigate = useNavigate();
  const [sermonText, setSermonText] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [copiedAll, setCopiedAll] = useState(false);
  const [genProgress, setGenProgress] = useState(0);
  const [genLabel, setGenLabel] = useState('');
  const [usage, setUsage] = useState(null);
  const [limitHit, setLimitHit] = useState(null);
  const dropRef = useRef(null);
  const progressRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/usage`, { headers: { ...getAuthHeaders() } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setUsage(d.usage?.social_posts); })
      .catch(() => {});
  }, []);

  const startProgress = () => {
    const steps = [
      { at: 0,  label: 'Reading transcript…' },
      { at: 18, label: 'Analysing themes…' },
      { at: 42, label: 'Writing posts…' },
      { at: 72, label: 'Polishing content…' },
    ];
    let val = 0;
    setGenProgress(0);
    setGenLabel(steps[0].label);
    progressRef.current = setInterval(() => {
      val += 0.7;
      const step = [...steps].reverse().find(s => val >= s.at);
      if (step) setGenLabel(step.label);
      setGenProgress(Math.min(val, 91));
      if (val >= 91) clearInterval(progressRef.current);
    }, 150);
  };

  const stopProgress = (success) => {
    clearInterval(progressRef.current);
    if (success) {
      setGenProgress(100);
      setGenLabel('Done!');
      setTimeout(() => { setGenProgress(0); setGenLabel(''); }, 700);
    } else {
      setGenProgress(0);
      setGenLabel('');
    }
  };

  const words = wordCount(sermonText);
  const tooShort = words > 0 && words < 100;

  const handleLogout = () => logout(navigate);

  // ── File extraction ──────────────────────────────────────────────────────
  const extractTextFromFile = async (file) => {
    setError('');
    setExtracting(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const isDocx = file.name?.endsWith('.docx') || file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      const isPdf  = file.type === 'application/pdf' || file.name?.endsWith('.pdf');
      if (isDocx) {
        const res = await mammoth.extractRawText({ arrayBuffer });
        setSermonText(res.value.trim());
      } else if (isPdf) {
        const pdf = await getDocument({ data: arrayBuffer }).promise;
        let text = '';
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const content = await page.getTextContent();
          text += content.items.map((item) => item.str).join(' ') + '\n';
        }
        setSermonText(text.trim());
      } else {
        setError('Unsupported file type. Please drop a .docx or .pdf file.');
      }
    } catch {
      setError('Could not extract text from the file. Please paste the text manually.');
    } finally {
      setExtracting(false);
      setIsDragging(false);
    }
  };

  const handleDrop      = (e) => { e.preventDefault(); setIsDragging(false); const f = e.dataTransfer.files?.[0]; if (f) extractTextFromFile(f); };
  const handleDragOver  = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e) => { if (!dropRef.current?.contains(e.relatedTarget)) setIsDragging(false); };

  // ── Generate ─────────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    setError('');
    setLimitHit(null);
    if (!sermonText.trim() || words < 100) return;
    setIsGenerating(true);
    setResult(null);
    startProgress();
    try {
      const response = await fetch(`${API}/content/generate-social`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ sermon_text: sermonText }),
      });
      if (response.ok) {
        stopProgress(true);
        setResult(await response.json());
        // Refresh usage count
        fetch(`${API}/usage`, { headers: { ...getAuthHeaders() } })
          .then(r => r.ok ? r.json() : null)
          .then(d => { if (d) setUsage(d.usage?.social_posts); })
          .catch(() => {});
      } else {
        stopProgress(false);
        const err = await response.json();
        if (response.status === 429) {
          setLimitHit(err.detail);
        } else {
          setError(err.detail || 'Failed to generate. Please try again.');
        }
      }
    } catch {
      stopProgress(false);
      setError('Something went wrong. Please check your connection and try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // ── Copy helpers ─────────────────────────────────────────────────────────
  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
  };

  const handleCopyOne = async (index, text) => {
    await copyText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const handleCopyAll = async () => {
    if (!result?.tweets) return;
    await copyText(result.tweets.map((t, i) => `${i + 1}. ${t}`).join('\n\n'));
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 2500);
  };

  return (
    <div className="min-h-screen" style={{ background: Y.bg }}>
      {/* Navbar */}
      <header className="sticky top-0 z-50 border-b" style={{ background: 'rgba(15,18,0,0.95)', backdropFilter: 'blur(8px)', borderColor: Y.border }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="flex items-center gap-1.5 text-sm transition-colors" style={{ color: Y.dim }}>
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Dashboard</span>
            </Link>
            <span style={{ color: Y.border }}>|</span>
            <Link to="/" className="font-serif text-xl font-bold text-white">SermonSift</Link>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:flex items-center gap-2 text-sm font-medium" style={{ color: Y.base }}>
              <XLogo size={14} /> Social Posts
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-2 rounded-lg transition-colors hover:bg-white/10">
                  <User className="w-5 h-5" style={{ color: Y.dim }} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Page header */}
        <div className="mb-8 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${Y.base}22` }}>
            <XLogo size={20} />
          </div>
          <div>
            <h1 className="text-xl font-serif font-bold text-white leading-none">Social Posts Generator</h1>
            <p className="text-xs mt-0.5" style={{ color: Y.dim }}>
              Paste a sermon transcript → generate 20 ready-to-post X / Twitter posts
            </p>
          </div>
        </div>

        {/* Input panel */}
        <div className="rounded-2xl p-5 mb-8 border" style={{ background: '#1a1800', borderColor: Y.border }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium" style={{ color: Y.text }}>Sermon Transcript</span>
            <span className="text-xs tabular-nums" style={{ color: tooShort ? '#f59e0b' : Y.dim }}>
              {words.toLocaleString()} words
            </span>
          </div>

          <div ref={dropRef} className="relative" onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}>
            {isDragging && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed pointer-events-none" style={{ borderColor: Y.base, background: `${Y.base}18` }}>
                <FileUp className="w-8 h-8" style={{ color: Y.base }} />
                <p className="text-sm font-semibold" style={{ color: Y.base }}>Drop to extract text</p>
                <p className="text-xs" style={{ color: Y.dim }}>.docx or .pdf</p>
              </div>
            )}
            {extracting && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-lg backdrop-blur-sm" style={{ background: 'rgba(15,18,0,0.8)' }}>
                <Loader2 className="w-6 h-6 animate-spin" style={{ color: Y.base }} />
                <p className="text-sm" style={{ color: Y.base }}>Extracting text…</p>
              </div>
            )}
            <textarea
              value={sermonText}
              onChange={(e) => setSermonText(e.target.value)}
              placeholder="Paste the full sermon transcript here… or drop a .docx / .pdf file"
              className="w-full rounded-lg p-4 min-h-[180px] resize-y border focus:outline-none transition-colors text-sm leading-relaxed"
              style={{ background: '#0f1200', color: Y.text, borderColor: Y.border, caretColor: Y.base }}
            />
          </div>

          {tooShort && <p className="mt-2 text-xs text-amber-400">⚠ Paste at least 100 words for best results.</p>}
          {error   && <p className="mt-2 text-xs text-red-400">{error}</p>}

          <div className="flex justify-end mt-3">
            <button
              onClick={handleGenerate}
              disabled={isGenerating || words < 100}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                background: isGenerating || words < 100 ? '#2a2500' : `linear-gradient(135deg, ${Y.base} 0%, ${Y.light} 100%)`,
                color: isGenerating || words < 100 ? Y.dim : '#1a1200',
              }}
            >
              {isGenerating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <><XLogo size={14} /> Generate 20 Posts</>}
            </button>
          </div>

          {/* Usage badge */}
          {usage && (
            <div className="flex items-center gap-1.5 text-xs mt-2 justify-end">
              {usage.unlimited
                ? <span style={{ color: Y.dim }}>Unlimited</span>
                : <span style={{ color: usage.remaining === 0 ? '#f87171' : usage.remaining <= 2 ? '#f59e0b' : Y.dim }}>
                    {usage.remaining} of {usage.limit} uses left this month
                  </span>
              }
            </div>
          )}

          {/* Limit alert */}
          {limitHit && (
            <div className="mt-3 rounded-xl border border-yellow-700 bg-yellow-950 p-4 text-center">
              <p className="text-sm font-semibold text-yellow-300 mb-1">Monthly limit reached</p>
              <p className="text-xs text-yellow-400 mb-3">{limitHit}</p>
              <Link to="/pricing">
                <button type="button" className="text-xs font-semibold px-4 py-2 rounded-lg bg-[#1e3a8a] text-white">
                  Upgrade Plan
                </button>
              </Link>
            </div>
          )}

          {isGenerating && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs" style={{ color: Y.dim }}>{genLabel}</span>
                <span className="text-xs font-bold tabular-nums" style={{ color: Y.base }}>{Math.round(genProgress)}%</span>
              </div>
              <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                <div
                  className="h-full rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${genProgress}%`, background: `linear-gradient(90deg, ${Y.base}, ${Y.light})` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        {result && (
          <div>
            {result.sermonTitle && (
              <p className="text-xs mb-4 text-center" style={{ color: Y.dim }}>
                Based on: <span className="italic" style={{ color: Y.text }}>"{result.sermonTitle}"</span>
              </p>
            )}

            <div className="flex items-center justify-between mb-5">
              <a
                href="https://x.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-xs transition-colors hover:opacity-80"
                style={{ color: Y.base }}
              >
                <XLogo size={13} /> Open X / Twitter
              </a>
              <button
                onClick={handleCopyAll}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium border transition-colors"
                style={{
                  background: '#1a1800',
                  borderColor: Y.border,
                  color: copiedAll ? '#4ade80' : Y.base,
                }}
              >
                {copiedAll ? <><CheckCircle className="w-3.5 h-3.5" /> Copied!</> : <><Copy className="w-3.5 h-3.5" /> Copy All 20</>}
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(result.tweets || []).map((tweet, i) => (
                <div
                  key={i}
                  className="rounded-xl p-4 border flex gap-3"
                  style={{ background: '#1a1800', borderColor: Y.border }}
                >
                  <span className="text-xs font-mono w-5 flex-shrink-0 pt-0.5" style={{ color: `${Y.base}60` }}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <p className="text-sm leading-relaxed flex-1" style={{ color: Y.text }}>{tweet}</p>
                  <div className="flex flex-col gap-1.5 flex-shrink-0">
                    <button
                      onClick={() => handleCopyOne(i, tweet)}
                      title="Copy post"
                      className="p-1.5 rounded-lg transition-colors hover:bg-white/10"
                      style={{ color: copiedIndex === i ? '#4ade80' : Y.base }}
                    >
                      {copiedIndex === i ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                    <a
                      href={`https://x.com/intent/tweet?text=${encodeURIComponent(tweet)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Post to X"
                      className="p-1.5 rounded-lg transition-colors hover:bg-white/10"
                      style={{ color: Y.dim }}
                    >
                      <XLogo size={14} />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
